import { useState, useRef, useCallback, useEffect } from 'react'

interface UseHoverMenuOptions {
    delay?: number
    closeTimeout?: number
    onShowPopup?: (isVisible: boolean) => void
}

interface UseHoverMenuReturn {
    showPopup: boolean
    setShowPopup: (show: boolean) => void
    onMouseEnter: () => void
    onMouseLeave: () => void
}

export function useHoverMenu(options: UseHoverMenuOptions = {}): UseHoverMenuReturn {
    const { delay = 500, closeTimeout = 5000 } = options
    const [showPopup, setShowPopup] = useState(false)
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const clearCloseTimer = useCallback(() => {
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current)
            closeTimerRef.current = null
        }
    }, [])

    const handleShowPopup = (newPopup: boolean) => {
        clearCloseTimer()
        setShowPopup(newPopup)
        if (options.onShowPopup) {
            options.onShowPopup(newPopup)
        }
    }

    useEffect(() => {
        if (showPopup && closeTimeout) {
            closeTimerRef.current = setTimeout(() => {
                setShowPopup(false)
            }, closeTimeout)
        }
        return clearCloseTimer
    }, [showPopup, closeTimeout, clearCloseTimer])

    const onMouseEnter = useCallback(() => {
        timerRef.current = setTimeout(() => {
            handleShowPopup(true)
        }, delay)
    }, [delay])

    const onMouseLeave = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current)
            timerRef.current = null
        }
    }, [])

    return {
        showPopup,
        setShowPopup: handleShowPopup,
        onMouseEnter,
        onMouseLeave,
    }
}
